import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useParams } from 'react-router-dom';
import { H2, Text } from '../components/ui/Typography';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import SafeIcon from '../common/SafeIcon';

const Scan = () => {
  const { lang = 'nl' } = useParams();
  const isEn = lang === 'en';
  const navigate = useNavigate();
  const [step, setStep] = useState(-1);
  const [answers, setAnswers] = useState({});
  const [analyzing, setAnalyzing] = useState(false);

  const questions = [
    {
      id: 'marital',
      q: isEn ? 'What is your current marital status?' : 'Wat is je huidige burgerlijke staat?',
      opts: isEn 
        ? ['Married / Registered Partnership', 'Cohabiting', 'Single']
        : ['Gehuwd / Geregistreerd', 'Samenwonend', 'Alleenstaand']
    },
    {
      id: 'children',
      q: isEn ? 'Do you have children?' : 'Heb je kinderen?',
      opts: isEn 
        ? ['Yes', 'No', 'Blended family']
        : ['Ja', 'Nee', 'Samengesteld gezin']
    },
    {
      id: 'business',
      q: isEn ? 'Do you own a business?' : 'Ben je eigenaar van een onderneming?',
      opts: isEn ? ['Yes', 'No'] : ['Ja', 'Nee']
    },
    {
      id: 'international',
      q: isEn ? 'Do you own property abroad or have a different nationality?' : 'Bezit je vastgoed in het buitenland of heb je een andere nationaliteit?',
      opts: isEn ? ['Yes', 'No'] : ['Ja', 'Nee']
    },
  ];

  const handleStart = () => setStep(0);

  const handleAnswer = (ans) => {
    setAnswers({ ...answers, [questions[step].id]: ans });
    if (step < questions.length - 1) {
      setStep(step + 1);
    } else {
      setStep(step + 1);
      setAnalyzing(true);
      setTimeout(() => setAnalyzing(false), 2000);
    }
  };

  const goToResult = () => {
    navigate(`/${lang}/testament-scan/resultaat`, { state: { answers } });
  };

  const renderContent = () => {
    if (step === -1) {
      return (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center py-10">
          <div className="w-16 h-16 bg-zinc-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <SafeIcon name="Activity" className="text-2xl text-zinc-900" />
          </div>
          <H2 className="mb-4">{isEn ? 'Testament Scan' : 'Testament Scan'}</H2>
          <Text className="mb-8 max-w-md mx-auto">
            {isEn 
              ? 'Answer a few simple questions and immediately discover which specific points of attention apply to your situation.'
              : 'Beantwoord een paar simpele vragen en ontdek direct welke specifieke aandachtspunten voor jouw situatie gelden.'}
          </Text>
          <Button onClick={handleStart} className="px-8 relative z-10">
            {isEn ? 'Start the analysis' : 'Start de analyse'}
          </Button>
        </motion.div>
      );
    }

    if (step < questions.length) {
      const q = questions[step];
      return (
        <motion.div key={step} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="py-10">
          <div className="text-xs font-medium text-zinc-400 mb-6 uppercase tracking-wider">
            {isEn ? `Question ${step + 1} of ${questions.length}` : `Vraag ${step + 1} van ${questions.length}`}
          </div>
          <h3 className="text-2xl font-semibold text-zinc-900 mb-8">{q.q}</h3>
          <div className="space-y-3">
            {q.opts.map(opt => (
              <button
                key={opt}
                onClick={() => handleAnswer(opt)}
                className="w-full text-left px-6 py-4 rounded-xl border border-zinc-200 hover:border-zinc-900 hover:bg-zinc-50 transition-all font-medium text-zinc-700 focus:outline-none focus:ring-2 focus:ring-zinc-900 focus:ring-offset-2 relative z-10"
              >
                {opt}
              </button>
            ))}
          </div>
        </motion.div>
      );
    }

    if (analyzing) {
      return (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center py-20 flex flex-col items-center">
          <motion.div animate={{ rotate: 360 }} transition={{ repeat: Infinity, duration: 1, ease: "linear" }} className="w-12 h-12 border-4 border-zinc-100 border-t-zinc-900 rounded-full mb-6" />
          <h3 className="text-xl font-medium text-zinc-900">{isEn ? 'Analyzing profile...' : 'Profiel analyseren...'}</h3>
          <p className="text-zinc-500 mt-2 text-sm">
            {isEn ? 'We are checking fiscal rules and legal complexities.' : 'We controleren fiscale regels en juridische complexiteiten.'}
          </p>
        </motion.div>
      );
    }

    return (
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="py-10">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-10 h-10 rounded-full bg-green-100 text-green-700 flex items-center justify-center">
            <SafeIcon name="Check" />
          </div>
          <div>
            <h3 className="text-2xl font-semibold">{isEn ? 'Analysis complete' : 'Analyse voltooid'}</h3>
            <p className="text-zinc-500 text-sm">{isEn ? 'Complexity score: Medium' : 'Complexiteitsscore: Gemiddeld'}</p>
          </div>
        </div>
        <div className="space-y-6 mb-10">
          <Card className="p-6 bg-zinc-50 border-none shadow-none">
            <h4 className="font-semibold text-zinc-900 mb-2 flex items-center gap-2">
              <SafeIcon name="AlertCircle" className="text-amber-500" /> {isEn ? 'Points of Attention' : 'Aandachtspunten'}
            </h4>
            <ul className="text-sm text-zinc-600 space-y-2 ml-6 list-disc">
              {isEn ? (
                <>
                  {(answers.business === 'Yes' || answers.business === 'Ja') && <li>Your business continuity must be guaranteed in a will.</li>}
                  {(answers.international === 'Yes' || answers.international === 'Ja') && <li>International law applies. Prevent double inheritance tax.</li>}
                  {((answers.marital !== 'Married / Registered Partnership' && answers.marital !== 'Gehuwd / Geregistreerd')) && <li>Without a will, everything goes to family, not to your partner.</li>}
                  {(answers.children === 'Blended family' || answers.children === 'Samengesteld gezin') && <li>Stepchildren do not inherit automatically. Specific clauses required.</li>}
                  {Object.values(answers).every(v => v === 'No' || v === 'Nee' || v === 'Single' || v === 'Alleenstaand') && <li>Standard legal distribution applies, but may not be optimal.</li>}
                </>
              ) : (
                <>
                  {(answers.business === 'Ja' || answers.business === 'Yes') && <li>Je bedrijfscontinuïteit moet gewaarborgd worden in een testament.</li>}
                  {(answers.international === 'Ja' || answers.international === 'Yes') && <li>Internationaal recht is van toepassing. Voorkom dubbele erfbelasting.</li>}
                  {(answers.marital !== 'Gehuwd / Geregistreerd' && answers.marital !== 'Married / Registered Partnership') && <li>Zonder testament gaat alles naar familie, niet naar je partner.</li>}
                  {(answers.children === 'Samengesteld gezin' || answers.children === 'Blended family') && <li>Stiefkinderen erven niet automatisch. Specifieke clausules vereist.</li>}
                  {Object.values(answers).every(v => v === 'Nee' || v === 'No' || v === 'Alleenstaand' || v === 'Single') && <li>Standaard wettelijke verdeling is van toepassing, maar mogelijk niet optimaal.</li>}
                </>
              )}
            </ul>
          </Card>
        </div>
        <div className="relative z-20">
          <Button onClick={goToResult} className="w-full py-4 text-base cursor-pointer pointer-events-auto" variant="primary">
            {isEn ? 'View full report & next steps' : 'Bekijk volledig rapport & vervolgstappen'}
          </Button>
        </div>
      </motion.div>
    );
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center py-20 px-6 bg-zinc-50">
      <Card className="max-w-xl w-full p-8 md:p-12 relative z-10 overflow-visible" hover={false}>
        <AnimatePresence mode="wait">
          {renderContent()}
        </AnimatePresence>
      </Card>
    </div>
  );
};

export default Scan;